import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getQuiz, submitQuiz, getQuizStats } from '../utils/api';
import toast from 'react-hot-toast';
import VictoryCelebration from '../components/VictoryCelebration';

export default function TrainingQuiz() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  async function load() {
    setLoading(true);
    try { const d = await getQuiz(id); setQuiz(d.quiz); setQuestions(d.questions || []); }
    catch (e) { setError(e?.response?.data?.error || 'Could not load quiz'); }
    finally { setLoading(false); }
  }

  async function handleSubmit() {
    const unanswered = questions.filter(q => answers[q.id] === undefined || answers[q.id] === '');
    if (unanswered.length) { toast.error(`Please answer all ${questions.length} questions`); return; }
    try {
      const r = await submitQuiz(id, answers);
      setResult(r);
      try { const s = await getQuizStats(id); setStats(s); } catch {}
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not submit'); }
  }

  if (loading) return <div className="page-body"><div style={{ padding: 30, color: '#aaa' }}>Loading…</div></div>;
  if (error) return <div className="page-body"><div style={{ padding: 30, maxWidth: 600 }}><div style={{ background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: 10, padding: 20, color: '#c62828' }}>{error}</div><button className="btn btn-outline" style={{ marginTop: 16 }} onClick={() => navigate('/training')}>← Back</button></div></div>;

  if (result) {
    const passed = result.passed;
    return (
      <div className="page-body">
        <div className={passed ? '' : 'try-again-shake'} style={{ maxWidth: 560, margin: '20px auto', background: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', border: '2px solid ' + (passed ? '#1B5E20' : '#c62828'), overflow: 'hidden' }}>
          {passed
            ? <VictoryCelebration score={result.score} pose="ok" />
            : <>
                <div className="fade-up-soft" style={{ fontSize: 48 }}>📚</div>
                <h2 className="fade-up-soft" style={{ color: '#c62828', margin: '10px 0' }}>Not quite yet</h2>
              </>}
          <div className={passed ? 'victory-score' : 'fade-up-soft'} style={{ fontSize: 44, fontWeight: 800, color: passed ? '#1B5E20' : '#c62828', marginTop: passed ? 6 : 0 }}>{result.score}%</div>
          <div className="fade-up-soft" style={{ color: '#777', marginBottom: 8 }}>{result.correct} of {result.total} correct · needed {result.passing_score}%</div>
          {stats?.team_average != null && (
            <div style={{ background: '#f5f7f6', borderRadius: 10, padding: 12, margin: '16px 0', fontSize: 14 }}>
              Team average: <strong>{stats.team_average}%</strong> <span style={{ color: '#999' }}>({stats.takers} {stats.takers === 1 ? 'person' : 'people'})</span>
            </div>
          )}
          {!passed && <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>{result.attempts_left > 0 ? `${result.attempts_left} attempt(s) left. Review the lessons and try again.` : 'No attempts left — ask a manager to reset.'}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button className="btn btn-primary" onClick={() => navigate('/training')}>Back to Training</button>
            {!passed && result.attempts_left > 0 && <button className="btn btn-outline" onClick={() => { setResult(null); setAnswers({}); load(); }}>Try again</button>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-header"><h2>{quiz?.title || 'Quiz'}</h2><button className="btn btn-outline btn-sm" onClick={() => navigate('/training')}>← Back</button></div>
      <div className="page-body">
        <div style={{ maxWidth: 700 }}>
          {questions.map((q, i) => (
            <div key={q.id} style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 14, border: '1px solid #eee' }}>
              <div style={{ fontWeight: 600, marginBottom: 12 }}>{i + 1}. {q.prompt}</div>
              {q.type === 'mc' && (q.options || []).map((opt, oi) => (
                <label key={oi} style={{ display: 'block', padding: '9px 12px', borderRadius: 8, marginBottom: 6, cursor: 'pointer', background: answers[q.id] === opt ? '#e8f3ec' : '#f7f7f7', border: '1px solid ' + (answers[q.id] === opt ? '#0D3B2E' : '#eee') }}>
                  <input type="radio" name={`q${q.id}`} checked={answers[q.id] === opt} onChange={() => setAnswers(a => ({ ...a, [q.id]: opt }))} style={{ marginRight: 10 }} />{opt}
                </label>
              ))}
              {q.type === 'tf' && ['true', 'false'].map(opt => (
                <label key={opt} style={{ display: 'inline-block', padding: '9px 18px', borderRadius: 8, marginRight: 10, cursor: 'pointer', background: answers[q.id] === opt ? '#e8f3ec' : '#f7f7f7', border: '1px solid ' + (answers[q.id] === opt ? '#0D3B2E' : '#eee'), textTransform: 'capitalize' }}>
                  <input type="radio" name={`q${q.id}`} checked={answers[q.id] === opt} onChange={() => setAnswers(a => ({ ...a, [q.id]: opt }))} style={{ marginRight: 8 }} />{opt}
                </label>
              ))}
              {q.type === 'text' && (
                <input type="text" value={answers[q.id] || ''} onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))}
                  placeholder="Type your answer" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: 8, fontSize: 14 }} />
              )}
            </div>
          ))}
          <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={handleSubmit}>Submit Quiz</button>
        </div>
      </div>
    </>
  );
}
